/**
 * Medication Service
 * 
 * This service handles medication database management, prescriptions, 
 * e-prescribing, and pharmacy operations.
 */

import apiClient from './apiClient';

class MedicationService {
    constructor() {
        this.baseURL = '/medications';
    }

    // ============================================================================
    // MEDICATION DATABASE MANAGEMENT
    // ============================================================================

    /**
     * Search medications in the database
     */
    async searchMedications(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/database/search`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Medications fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to search medications",
                data: []
            };
        }
    }

    /**
     * Get medication by ID
     */
    async getMedicationById(medicationId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/database/${medicationId}`);
            return {
                success: true,
                data: response.data,
                message: "Medication fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch medication",
                data: null
            };
        }
    }

    /**
     * Add medication to database (Admin only)
     */
    async createMedication(medicationData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/database`, medicationData);
            return {
                success: true,
                data: response.data,
                message: "Medication added successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to add medication",
                data: null
            };
        }
    }

    // ============================================================================
    // PRESCRIPTION MANAGEMENT
    // ============================================================================

    /**
     * Create new prescription
     */
    async createPrescription(prescriptionData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/prescriptions`, prescriptionData);
            return {
                success: true,
                data: response.data,
                message: "Prescription created successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to create prescription",
                data: null
            };
        }
    }

    /**
     * Get list of prescriptions with filtering
     */
    async getPrescriptions(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/prescriptions`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Prescriptions fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch prescriptions",
                data: []
            };
        }
    }

    /**
     * Get prescription by ID
     */
    async getPrescriptionById(prescriptionId) {
        try {
            const response = await apiClient.get(`${this.baseURL}/prescriptions/${prescriptionId}`);
            return {
                success: true,
                data: response.data,
                message: "Prescription fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch prescription",
                data: null
            };
        }
    }

    /**
     * Approve prescription
     */
    async approvePrescription(prescriptionId, approvalData = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/prescriptions/${prescriptionId}/approve`, approvalData);
            return {
                success: true,
                data: response.data,
                message: "Prescription approved successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to approve prescription",
                data: null
            };
        }
    }

    /**
     * Dispense prescription
     */
    async dispensePrescription(prescriptionId, dispenseData) {
        try {
            const response = await apiClient.post(`${this.baseURL}/prescriptions/${prescriptionId}/dispense`, dispenseData);
            return {
                success: true,
                data: response.data,
                message: "Prescription dispensed successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to dispense prescription",
                data: null
            };
        }
    }

    /**
     * Perform safety checks on prescription
     */
    async performSafetyChecks(prescriptionId) {
        try {
            const response = await apiClient.post(`${this.baseURL}/prescriptions/${prescriptionId}/safety-check`);
            return {
                success: true,
                data: response.data,
                message: "Safety checks completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Safety checks failed",
                data: null
            };
        }
    }

    /**
     * Digitally sign prescription
     */
    async signPrescription(prescriptionId, signatureData = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/prescriptions/${prescriptionId}/sign`, signatureData);
            return {
                success: true,
                data: response.data,
                message: "Prescription signed successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to sign prescription",
                data: null
            };
        }
    }

    /**
     * Update prescription status
     */
    async updatePrescriptionStatus(prescriptionId, statusData) {
        try {
            const response = await apiClient.patch(`${this.baseURL}/prescriptions/${prescriptionId}/status`, statusData);
            return {
                success: true,
                data: response.data,
                message: "Prescription status updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update prescription status",
                data: null
            };
        }
    }

    // ============================================================================
    // ANALYTICS AND METRICS
    // ============================================================================

    /**
     * Get prescription metrics
     */
    async getPrescriptionMetrics(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/analytics/metrics`, { params });
            return {
                success: true,
                data: response.data,
                message: "Prescription metrics fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch prescription metrics",
                data: null
            };
        }
    }

    // ============================================================================
    // LEGACY COMPATIBILITY
    // ============================================================================

    /**
     * Get MedPass prescription list (Legacy format)
     */
    async getMedpassPrescriptionList(data = {}) {
        try {
            const response = await apiClient.post(`${this.baseURL}/getMedpassPrescriptionList`, data);
            return {
                success: true,
                data: response.data.data || response.data,
                message: "MedPass prescription list fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch MedPass prescription list",
                data: []
            };
        }
    }

    // ============================================================================
    // DOH MEDICATION METHODS (For Patient-Context MedPass)
    // ============================================================================

    /**
     * Search DOH-approved medications for patient-context workflows
     */
    async searchDOHApprovedMedications(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/doh-approved/search`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "DOH medications fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to search DOH medications",
                data: []
            };
        }
    }

    /**
     * Typeahead search for DOH-approved medications (min 2 characters)
     */
    async typeaheadDOHApprovedMedications(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseURL}/doh-approved/typeahead`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "DOH medications typeahead completed successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to perform typeahead search",
                data: []
            };
        }
    }

    /**
     * Get current medications for a patient
     */
    async getPatientMedications(patientId, params = {}) {
        try {
            const response = await apiClient.get(`/patients/${patientId}/current-medications`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Patient medications fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch patient medications",
                data: []
            };
        }
    }

    /**
     * Get patient safety alerts for medication ordering
     */
    async getPatientSafetyAlerts(patientId) {
        try {
            const response = await apiClient.get(`/medpass/patient-context/${patientId}/safety-alerts`);
            return {
                success: true,
                data: response.data || [],
                message: "Safety alerts fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch safety alerts",
                data: []
            };
        }
    }

    /**
     * Validate medication selection for patient safety
     */
    async validateMedicationSelection(patientId, medications) {
        try {
            const response = await apiClient.post(`${this.baseURL}/validate-selection`, {
                patient_id: patientId,
                medications: medications
            });
            return {
                success: true,
                data: response.data,
                message: "Medication validation completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to validate medication selection",
                data: null
            };
        }
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Check drug interactions
     */
    async checkDrugInteractions(medicationIds) {
        try {
            const response = await apiClient.post(`${this.baseURL}/check-interactions`, {
                medication_ids: medicationIds
            });
            return {
                success: true,
                data: response.data,
                message: "Drug interaction check completed"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to check drug interactions",
                data: null
            };
        }
    }

    /**
     * Get medication history for patient
     */
    async getPatientMedicationHistory(patientId, params = {}) {
        try {
            const response = await apiClient.get(`/patients/${patientId}/medications`, { params });
            return {
                success: true,
                data: response.data || [],
                message: "Medication history fetched successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to fetch medication history",
                data: []
            };
        }
    }
}

export default new MedicationService(); 